// The settings pages, grouped, with their role and feature gates.
//
// Shared by the app bar's gear panel (MyAppBar) and the in-page sub-nav
// (SettingsNav) so a page can never show up in one and be missing from the
// other — the old flat menu spelled the list twice, once per role branch, and
// the two had already drifted.
export const SETTINGS_GROUPS = [
  {
    title: 'People & places',
    items: [
      { path: '/settings/customers', label: 'Customers', roles: ['admin', 'manager'] },
      { path: '/settings/operators', label: 'Operators', roles: ['admin', 'manager'] },
      { path: '/settings/users', label: 'Users', roles: ['admin'] },
      { path: '/settings/locations', label: 'Locations', roles: ['admin', 'manager'] },
      { path: '/settings/label-stocks', label: 'Label stocks', roles: ['admin', 'manager'] },
    ],
  },
  {
    title: 'Templates',
    items: [
      { path: '/settings/task-templates', label: 'Task templates', roles: ['admin', 'manager'] },
      { path: '/settings/asset-templates', label: 'Asset templates', roles: ['admin', 'manager'], feature: 'asset_tracking' },
      { path: '/settings/email-templates', label: 'Email templates', roles: ['admin'] },
      { path: '/settings/pdf-templates', label: 'PDF templates', roles: ['admin'] },
    ],
  },
  {
    title: 'Platform',
    items: [
      { path: '/settings/security', label: 'Security', roles: ['admin'] },
      { path: '/settings/sso', label: 'Single sign-on', roles: ['admin'], feature: 'sso' },
      { path: '/settings/api-keys', label: 'API keys', roles: ['admin'], anyFeature: ['api_access', 'mcp_access'] },
      { path: '/settings/integrations', label: 'Integrations', roles: ['admin'], feature: 'integrations' },
      { path: '/settings/sla', label: 'SLA configuration', roles: ['admin', 'manager'], feature: 'issue_tracking' },
      { path: '/settings/features', label: 'Features', roles: ['admin'] },
      { path: '/settings/scheduled-reports', label: 'Scheduled reports', roles: ['admin', 'manager'] },
      { path: '/settings/usage', label: 'Usage', roles: ['admin', 'manager'] },
    ],
  },
];

/**
 * Whether one menu item should be shown.
 *
 * Lives here rather than in each consumer because MyAppBar and SettingsNav
 * both filter the same list, and an item gated in one place but not the other
 * is exactly the drift SETTINGS_GROUPS exists to prevent. `feature` gates on
 * one flag; `anyFeature` on holding at least one of several — the API keys page
 * serves two surfaces (REST and MCP) with a separate flag each, and a workspace
 * with either one needs the page.
 *
 * @param {object} item      a SETTINGS_GROUPS entry
 * @param {string} role      the viewer's role in the current workspace
 * @param {object} features  { [flag]: boolean } for the current workspace
 */
export function isSettingsItemVisible(item, role, features = {}) {
  if (!item.roles.includes(role)) return false;
  if (item.anyFeature) return item.anyFeature.some((f) => features[f] === true);
  if (item.feature) return features[item.feature] === true;
  return true;
}

/** SETTINGS_GROUPS with hidden items and emptied groups removed. */
export function visibleSettingsGroups(role, features = {}) {
  return SETTINGS_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isSettingsItemVisible(item, role, features)),
    }))
    .filter((group) => group.items.length > 0);
}

export default SETTINGS_GROUPS;
